/**
 * Публічна реєстрація: акаунт + перший користувач (власник акаунта).
 *
 * ч.1122 (рішення власника): реєстрація з підтвердженням пошти. Форма лише
 * надсилає запит — на пошту йде лист із посиланням, а обліковий запис
 * створюється після підтвердження (сторінка /signup/confirm). Раніше акаунт
 * зʼявлявся одразу, і цим користувались боти: реєстрували чужі пошти й слали
 * на них листи через «Забули пароль?».
 */
import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { checkTesterLink, signup as apiSignup, type SignupAccepted } from '@/api/auth';
import { useAuth } from '@/auth/store';
import LegalConsent from '@/shared/components/LegalConsent';

type ApiError = { response?: { status?: number; data?: { detail?: string } } };

function errorText(e: unknown): string {
  const res = (e as ApiError)?.response;
  if (res?.status === 409) {
    return 'Email уже зареєстровано — увійдіть або відновіть пароль («Забули пароль?» нижче)';
  }
  if (res?.status === 422) {
    return 'Перевірте формат email і довжину пароля (мін. 8 символів)';
  }
  return res?.data?.detail ?? 'Помилка реєстрації';
}

export default function SignupPage() {
  const navigate = useNavigate();
  const setSession = useAuth((s) => s.setSession);
  const setActiveTenant = useAuth((s) => s.setActiveTenant);
  // ч.1126: посилання для тестувальників (`/signup?code=…`, видає власник
  // платформи) — акаунт створюється одразу, без листа. Недійсне посилання не
  // ховаємо: людина має знати, що реєструється звичайним способом.
  const [params] = useSearchParams();
  const testerCode = params.get('code');
  const tester = useQuery({
    queryKey: ['tester-link', testerCode],
    queryFn: () => checkTesterLink(testerCode!),
    enabled: Boolean(testerCode),
    retry: false,
    staleTime: Infinity,
  });
  const byTesterLink = Boolean(testerCode) && tester.data?.valid === true;
  const [email, setEmail] = useState('');
  const [accepted, setAccepted] = useState(false);
  // ч.1127: за посиланням для тестувальників листа немає, а акаунт лишається
  // робочим і після тестування — одруківку в адресі ніщо не зловить, тому
  // адресу вводять двічі (на неї приходитиме відновлення пароля).
  const [emailRepeat, setEmailRepeat] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [fullName, setFullName] = useState('');
  const [tenantName, setTenantName] = useState('');
  // Поле-пастка: людина його не бачить і не заповнює; автомат, що заповнює всі
  // поля форми, видає себе — сервер такий запит мовчки відкидає.
  const [trap, setTrap] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<SignupAccepted | null>(null);
  const [resent, setResent] = useState(false);

  async function send(): Promise<boolean> {
    setError(null);
    setLoading(true);
    try {
      const res = await apiSignup({
        email: email.trim(),
        password,
        full_name: fullName.trim(),
        tenant_name: tenantName.trim(),
        accept_terms: true,
        ...(trap ? { contact_time_pref: trap } : {}),
        ...(byTesterLink ? { tester_code: testerCode! } : {}),
      });
      if ('access_token' in res) {
        // За посиланням для тестувальників акаунт уже створено — входимо одразу.
        setSession(res.user_id ?? null);
        setActiveTenant(null);
        navigate('/select-tenant', { replace: true, state: { next: '/account' } });
        return true;
      }
      setSent(res);
      return true;
    } catch (e: unknown) {
      setError(errorText(e));
      return false;
    } finally {
      setLoading(false);
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!accepted) {
      setError('Щоб продовжити, прийміть Умови використання і Політику конфіденційності');
      return;
    }
    if (password.length < 8) {
      setError('Пароль має містити мінімум 8 символів');
      return;
    }
    if (password !== passwordConfirm) {
      setError('Паролі не співпадають');
      return;
    }
    if (byTesterLink && email.trim().toLowerCase() !== emailRepeat.trim().toLowerCase()) {
      setError('Адреси пошти не збігаються — перевірте, чи немає одруківки');
      return;
    }
    setResent(false);
    await send();
  }

  async function onResend() {
    setResent(false);
    if (await send()) setResent(true);
  }

  if (sent) {
    return (
      <div className="login">
        <form onSubmit={(e) => e.preventDefault()}>
          <h1>Перевірте пошту</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
            Ми надіслали лист на <strong>{sent.email}</strong>. Відкрийте
            посилання з листа, щоб завершити реєстрацію, — воно
            діє {sent.expires_in_hours} год. і спрацьовує один раз.
          </p>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
            Обліковий запис буде створено після підтвердження. Не бачите
            листа — перевірте папку «Спам» або надішліть його ще раз.
          </p>
          <button type="button" className="primary" style={{ width: '100%' }}
            onClick={onResend} disabled={loading}>
            {loading ? 'надсилання…' : 'надіслати лист ще раз'}
          </button>
          {resent && !error && (
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
              Лист надіслано ще раз. Чинне лише посилання з останнього листа.
            </p>
          )}
          {error && <div className="error">{error}</div>}
          <p style={{ marginTop: '1rem', fontSize: '0.85rem', textAlign: 'center' }}>
            <button type="button" className="btn-link"
              onClick={() => { setSent(null); setResent(false); setError(null); }}>
              змінити дані
            </button>
            {' · '}
            <Link to="/login">Повернутися до входу</Link>
          </p>
        </form>
      </div>
    );
  }

  return (
    <div className="login">
      <form onSubmit={onSubmit}>
        <h1>Створення акаунта</h1>

        {byTesterLink && (
          <p style={{ marginTop: 0, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
            Реєстрація за посиланням для тестувальників: підтверджувати пошту не
            потрібно, акаунт буде створено одразу.
          </p>
        )}
        {testerCode && tester.data?.valid === false && (
          <div className="error" style={{ marginBottom: '0.75rem' }}>
            Посилання для тестувальників недійсне, вичерпане або відкликане. Попросіть
            нове — або зареєструйтесь звичайним способом, з підтвердженням пошти.
          </div>
        )}

        <div className="field">
          <label>Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
            placeholder="you@company.com"
          />
        </div>

        {byTesterLink && (
          <div className="field">
            <label htmlFor="signup-email-repeat">Email ще раз</label>
            <input
              id="signup-email-repeat"
              type="email"
              value={emailRepeat}
              onChange={(e) => setEmailRepeat(e.target.value)}
              required
              placeholder="you@company.com"
            />
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
              Листа з підтвердженням не буде, тому перевірте адресу: на неї
              приходитиме відновлення пароля.
            </div>
          </div>
        )}

        <div className="field">
          <label>Ваше ПІБ</label>
          <input
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            required
            placeholder="Іванов Іван Іванович"
          />
        </div>

        <div className="field">
          <label>Назва акаунта</label>
          <input
            value={tenantName}
            onChange={(e) => setTenantName(e.target.value)}
            required
            placeholder='ТОВ "Альфа" / ФОП Іваненко / Моя компанія'
          />
          {/* ч.1072: прибрано «Можна змінити пізніше у налаштуваннях» —
              перейменування акаунта в системі немає, обіцянка була хибною. */}
        </div>

        {/* Поле-пастка (ч.1122): поза екраном, поза порядком Tab і невидиме для
            програм читання з екрана. Назва навмисно не схожа на те, що браузер
            заповнює сам (імʼя, адреса, компанія). */}
        <div className="signup-trap" aria-hidden="true">
          <label htmlFor="contact_time_pref">Зручний час для звʼязку</label>
          <input
            id="contact_time_pref"
            name="contact_time_pref"
            type="text"
            value={trap}
            onChange={(e) => setTrap(e.target.value)}
            tabIndex={-1}
            autoComplete="off"
          />
        </div>

        <div className="field">
          <label>Пароль (мін. 8 символів)</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
          />
        </div>

        <div className="field">
          <label>Повторіть пароль</label>
          <input
            type="password"
            value={passwordConfirm}
            onChange={(e) => setPasswordConfirm(e.target.value)}
            required
          />
        </div>

        <LegalConsent checked={accepted} onChange={setAccepted} />

        <button
          type="submit"
          className="primary"
          style={{ width: '100%' }}
          disabled={loading}
        >
          {loading ? 'Надсилаємо лист…' : 'Зареєструватися'}
        </button>

        {error && <div className="error">{error}</div>}

        {!byTesterLink && (
          <p style={{ marginTop: '1rem', fontSize: '0.8rem', color: 'var(--text-secondary)', textAlign: 'center' }}>
            На пошту прийде лист із посиланням — обліковий запис буде створено
            після підтвердження.
          </p>
        )}

        <p style={{ marginTop: '1rem', fontSize: '0.85rem', textAlign: 'center' }}>
          Уже маєте акаунт? <Link to="/login">Увійти</Link>
          {' · '}
          <Link to={`/forgot-password${email.trim()
            ? `?email=${encodeURIComponent(email.trim())}` : ''}`}>Забули пароль?</Link>
        </p>
      </form>
    </div>
  );
}
